import { createSelector } from '@reduxjs/toolkit';

import { GlobalState } from '@/redux/store';
import { emptyItemSummary, ItemSummary } from '@/types/item/ItemSummary';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { JASPER52_ID } from '@/utils/houseIds.constants';
import { getImageUrl, getItemUrlFromSlug, getSellerUrl, imagePlaceholder } from '@/utils/urls';

import { ITEM_SUMMARY_CACHE_TIME, NextAndPreviousItemResponse } from './itemSummary.types';

const stateSelector = (state: GlobalState) => state;
const itemSummarySlice = createSelector(stateSelector, (state) => state.itemSummary);

const idSelector = (_: GlobalState, id: number) => id;
const secondIdSelector = (_1: GlobalState, _2: number, id: number) => id;
const idsSelector = (_: GlobalState, ids: number[]) => ids;

const byIdSelector = createSelector(itemSummarySlice, ({ byId }) => byId);
const loadedSelector = createSelector(itemSummarySlice, ({ loaded }) => loaded);
const loadingSelector = createSelector(itemSummarySlice, ({ loading }) => loading);

export const getAllItemSummaries = createSelector(byIdSelector, (summaries) => summaries);

const getItemSummary = createSelector([byIdSelector, idSelector], (byId, itemId) => byId[itemId] || emptyItemSummary);

export const getItemSummaries = createSelector([byIdSelector, idsSelector], (byId, itemIds) =>
    (itemIds || []).map((itemId: number) => byId[itemId]).filter((item) => Boolean(item))
);

// Used for Hammer UI CardGrid data to render cards.
export const getItemSummaryMap = createSelector([byIdSelector, idsSelector], (byId, itemIds) =>
    (itemIds || []).reduce(
        (acc, itemId: number, arrayIndex: number) => {
            if (byId[itemId]) {
                // Add card-itemId to the item summary to be used to control hover states
                acc[itemId] = { ...byId[itemId], id: `card-${itemId}`, position: arrayIndex };
            }
            return acc;
        },
        {} as Record<number, ItemSummary & { id?: string; position: number }>
    )
);

export const getItemSummaryIsPlacingBid = createSelector(
    getItemSummary,
    ({ bidderIsPlacingBid }) => bidderIsPlacingBid
);

export const getItemSummaryIsExtended = createSelector(
    getItemSummary,
    (item) => item && item.isTimedPlusAuction && item.isExtended
);
export const getItemSummaryCurrentTimedPlusEndTime = createSelector(
    getItemSummary,
    /**
     * @returns Timed+ Item's end time in epoch seconds
     */
    ({ lotEndTimeEstimatedTs }) => lotEndTimeEstimatedTs
);

export const getItemSummaryBidActivityCount = createSelector(
    getItemSummary,
    ({ bidActivityCount }) => bidActivityCount
);
export const getItemSummaryBidCount = createSelector(getItemSummary, ({ bidCount }) => bidCount);
export const getItemSummaryBidderBidTimestamp = createSelector(
    getItemSummary,
    ({ bidderBidTimestamp }) => bidderBidTimestamp
);
export const getItemSummaryLeadingBid = createSelector(getItemSummary, ({ leadingBid }) => leadingBid);
export const getItemSummaryBuyNowStatus = createSelector(getItemSummary, ({ buyNowStatus }) => buyNowStatus);
export const getItemSummaryBuyNowAvailable = createSelector(getItemSummary, ({ buyNowStatus }) =>
    Boolean(buyNowStatus)
);
export const getItemSummaryBuyNowPrice = createSelector(getItemSummary, ({ buyNowPrice }) => buyNowPrice);
export const getItemSummaryCurrency = createSelector(getItemSummary, ({ currency }) => currency);

export const getItemSummaryCatalogStatus = createSelector(getItemSummary, ({ catalogStatus }) => catalogStatus);
export const getItemSummaryCatalogOnly = createSelector(getItemSummary, ({ isCatalogOnly }) => isCatalogOnly);
export const getItemSummaryCatalogLotsListed = createSelector(getItemSummary, ({ lotsListed }) => lotsListed);
export const getItemSummaryCatalogId = createSelector(getItemSummary, ({ catalogId }) => catalogId);
export const getItemSummaryCatalogTitle = createSelector(getItemSummary, ({ catalogTitle }) => catalogTitle);
export const getItemSummaryIsCatalogOnly = createSelector(getItemSummary, ({ isCatalogOnly }) => isCatalogOnly);

export const getItemSummarySellerId = createSelector(getItemSummary, ({ sellerId }) => sellerId);
export const getItemSummarySellerIsJasper52 = createSelector(
    getItemSummary,
    ({ sellerId }) => sellerId === JASPER52_ID
);
export const getItemSummarySellerName = createSelector(getItemSummary, ({ sellerName }) => sellerName);

export const getItemSummaryItemId = createSelector(getItemSummary, ({ itemId }) => itemId);
export const getItemSummaryHasReserve = createSelector(getItemSummary, ({ hasReserve }) => hasReserve);
export const getItemSummaryIsReserveMet = createSelector(getItemSummary, ({ isReserveMet }) => isReserveMet);
export const getItemSummaryTitle = createSelector(getItemSummary, ({ title }) => title);
export const getItemSummarySEOExcluded = createSelector(getItemSummary, ({ isSEOExcluded }) => isSEOExcluded);
export const getItemSummaryLotNumber = createSelector(getItemSummary, ({ lotNumber }) => lotNumber);
export const getItemSummaryPromoted = createSelector(getItemSummary, ({ promoted }) => promoted);
export const getItemSummaryIsRestricted = createSelector(
    getItemSummary,
    ({ restrictedCategories }) => restrictedCategories && restrictedCategories.adult
);
export const getItemSummarySaleStartTs = createSelector(getItemSummary, ({ saleStartTs }) => saleStartTs);

export const getItemSummaryHighBidEstimate = createSelector(getItemSummary, ({ highBidEstimate }) => highBidEstimate);
export const getItemSummaryLowBidEstimate = createSelector(getItemSummary, ({ lowBidEstimate }) => lowBidEstimate);
export const getItemSummaryStartPrice = createSelector(getItemSummary, ({ startPrice }) => startPrice);
export const getItemSummarySalePrice = createSelector(getItemSummary, ({ salePrice }) => salePrice);

export const getItemSummarySlug = createSelector(getItemSummary, ({ slug }) => slug);
export const getItemSummarySlugWithLocation = createSelector(
    getItemSummary,
    ({ slugWithLocation }) => slugWithLocation
);

export const getItemSummaryIsAvailable = createSelector(getItemSummary, ({ isAvailable }) => isAvailable);
export const getItemSummaryIsSold = createSelector(getItemSummary, ({ isSold }) => isSold);
export const getItemSummaryIsLocked = createSelector(getItemSummary, ({ isLocked }) => isLocked);
export const getItemSummaryIsPassed = createSelector(getItemSummary, ({ isPassed }) => isPassed);
export const getItemSummaryIsPaused = createSelector(getItemSummary, ({ isPaused }) => isPaused);
export const getItemSummaryIsDeleted = createSelector(getItemSummary, ({ isDeleted }) => isDeleted);

export const getItemSummarySimilarItemsUrl = createSelector(getItemSummary, ({ similarItemsURL }) => similarItemsURL);
export const getItemSummaryShortDescription = createSelector(
    getItemSummary,
    ({ shortDescription }) => shortDescription
);
export const getItemSummaryStartTime = createSelector(getItemSummary, ({ saleStartTs }) => saleStartTs);
export const hasItemSummaryStarted = createSelector(
    getItemSummaryStartTime,
    (saleStartTs) => Date.now() / 1000 > saleStartTs
);

export const getItemSummaryBidderHasBid = createSelector(getItemSummary, ({ bidderHasBid }) => bidderHasBid);
export const getItemSummaryBidderMaxBid = createSelector(getItemSummary, ({ bidderMaxBid }) => bidderMaxBid);
export const getItemSummaryBidderWonItem = createSelector(getItemSummary, ({ bidderWonItem }) => bidderWonItem);
export const getItemSummaryBidderHasHighBid = createSelector(
    getItemSummary,
    ({ bidderHasHighBid }) => bidderHasHighBid
);

export const getItemSummaryLotLocation = createSelector(
    getItemSummary,
    ({ lotLocation }) => lotLocation || emptyItemSummary.lotLocation
);

export const getItemSummaryHasLiveShipping = createSelector(getItemSummary, ({ hasLiveShipping }) => hasLiveShipping);
export const getItemSummaryHasFreeShipping = createSelector(getItemSummary, ({ hasFreeShipping }) => hasFreeShipping);
export const getItemSummaryHasFlatRateShipping = createSelector(
    getItemSummary,
    ({ hasFlatRateShipping }) => hasFlatRateShipping
);
export const getItemSummaryFlatRateShippingAmount = createSelector(
    getItemSummary,
    ({ flatRateShippingAmount }) => flatRateShippingAmount ?? 0
);

export const getItemSummaryUrl = createSelector(
    [getItemSummarySlugWithLocation, idSelector],
    (slugWithLocation, itemId) => getItemUrlFromSlug(itemId, slugWithLocation)
);
export const getItemSummarySellerUrl = createSelector(
    [getItemSummarySellerName, getItemSummarySellerId],
    (name, sellerId) => getSellerUrl(sellerId, name)
);

export const getItemSummaryIsLiveAuction = createSelector(getItemSummary, ({ isLiveAuction }) => isLiveAuction);
export const getItemSummaryIsTimedAuction = createSelector(getItemSummary, ({ isTimedAuction }) => isTimedAuction);
export const getItemSummaryIsTimedPlusAuction = createSelector(
    getItemSummary,
    ({ isTimedPlusAuction }) => isTimedPlusAuction
);

export const getItemSummaryLotEndTimeEstimatedTs = createSelector(
    getItemSummary,
    ({ lotEndTimeEstimatedTs }) => lotEndTimeEstimatedTs
);
export const getIsItemSummaryTimedPlusBiddingClosed = createSelector(
    [
        getItemSummaryIsTimedPlusAuction,
        getItemSummaryCurrentTimedPlusEndTime,
        getItemSummaryIsSold,
        getItemSummaryIsPassed,
    ],
    (isTimedPlusAuction, lotEndTimeEstimatedTs, isSold, isPassed) =>
        (isTimedPlusAuction && lotEndTimeEstimatedTs < Date.now() / 1000) || isSold || isPassed
);
export const getIsItemSummaryTimedPlusBiddingOpen = createSelector(
    getItemSummaryStartTime,
    (saleStartTs) => Boolean(saleStartTs) && Date.now() / 1000 > saleStartTs
);

export const getItemSummaryPhotos = createSelector(getItemSummary, ({ photos }) => photos || []);
export const getItemSummaryPhotosCount = createSelector(getItemSummaryPhotos, (photos) => photos?.length);
export const getItemSummaryFirstImageId = createSelector(getItemSummaryPhotos, (photos) => photos?.[0]);
export const getItemSummaryIndex = createSelector(getItemSummary, ({ index }) => index);
export const getItemSummaryImageVersion = createSelector(getItemSummary, ({ imageVersion }) => imageVersion);

export const getItemSummaryFirstImageUrl = createSelector(
    [
        getItemSummaryCatalogId,
        getItemSummaryFirstImageId,
        getItemSummaryImageVersion,
        getItemSummarySellerId,
        idSelector,
    ],
    (catalogId, imageNumber, imageVersion, sellerId, itemId) => {
        if (typeof imageNumber === 'undefined') {
            return imagePlaceholder;
        }
        return getImageUrl({
            catalogId,
            imageNumber,
            imageVersion,
            itemId,
            sellerId,
        });
    }
);

export const getItemSummaryImageUrlByImageNumber = createSelector(
    [getItemSummaryCatalogId, getItemSummaryImageVersion, getItemSummarySellerId, idSelector, secondIdSelector],
    (catalogId, imageVersion, sellerId, itemId, imageNumber) => {
        return getImageUrl({
            catalogId,
            imageNumber,
            imageVersion,
            itemId,
            sellerId,
        });
    }
);

export const getIsItemSummaryLoaded = createSelector([loadedSelector, idSelector], (loaded, id) =>
    loaded.hasOwnProperty(id)
);
export const getIsItemSummaryLoading = createSelector([loadingSelector, idSelector], (loading, id) =>
    loading.includes(id)
);
export const getLoadTimeForItemSummary = createSelector(
    [loadedSelector, idSelector],
    (loaded, itemId) => loaded[itemId] || 0
);

/**
 * @returns number the available shipping provider's ID, or 0 there is no shipping provider
 */
export const getItemSummaryShippingProviderId = createSelector(
    getItemSummary,
    ({ shippingProviderId }) => shippingProviderId || 0
);

export const getItemIdsForCatalog = createSelector([byIdSelector, idSelector], (byId, id): number[] =>
    Object.keys(byId)
        .map((key) => byId[Number(key)])
        .filter((item) => item.catalogId === id)
        .map((item) => item.itemId)
);

export const getItemSummariesForCatalog = createSelector([byIdSelector, idSelector], (byId, id): ItemSummary[] =>
    Object.keys(byId)
        .map((key) => byId[Number(key)])
        .filter((item) => item.catalogId === id)
        .sort((a, b) => a.index - b.index)
);

export const getItemIdsForCatalogWithMostBids = createSelector([byIdSelector, idSelector], (byId, id): number[] =>
    Object.keys(byId)
        .map((key) => byId[Number(key)])
        .filter((item) => item.catalogId === id)
        .sort((a, b) => b.bidActivityCount - a.bidActivityCount)
        .map((item) => item.itemId)
        .slice(0, 10)
);

export const getItemIdsForSeller = createSelector([byIdSelector, idSelector], (byId, id): number[] =>
    Object.keys(byId)
        .map((key) => byId[key])
        .filter((item) => item.sellerId === id)
        .map((item) => item.itemId)
);

export const getNextAndPreviousItemSummaries = createSelector(
    [byIdSelector, idSelector],
    (byId, itemId): NextAndPreviousItemResponse => {
        const currentItem = byId[itemId];
        if (!currentItem) {
            return {
                next: 0,
                previous: 0,
            };
        }
        const { catalogId, index, lotsListed } = currentItem;

        const items = Object.keys(byId)
            .map((key) => byId[key])
            .filter((item) => item.catalogId === catalogId);

        let previous: number;
        let next: number;

        if (index === 0) {
            next = items.find((item) => item.index === index + 1)?.itemId;
        } else if (index === lotsListed - 1) {
            previous = items.find((item) => item.index === index - 1)?.itemId;
        } else {
            previous = items.find((item) => {
                return item.index === index - 1;
            })?.itemId;
            next = items.find((item) => {
                return item.index === index + 1;
            })?.itemId;
        }

        return {
            next: next || 0,
            previous: previous || 0,
        };
    }
);

export const getNextItemSummary = createSelector([byIdSelector, idSelector], (byId, itemId) => {
    const currentItem = byId[itemId];

    let next: ItemSummary = emptyItemSummary;

    if (!currentItem) {
        return next;
    }

    const { catalogId, index, lotsListed } = currentItem;
    const items = Object.keys(byId)
        .map((key) => byId[key])
        .filter((item) => item.catalogId === catalogId);

    if (index >= 0 && lotsListed - 1 > index) {
        next = items.find((item) => item.index === index + 1) || emptyItemSummary;
    }

    return next;
});

export const shouldFetchItemSummary = (state: GlobalState, itemId: number): boolean => {
    if (!itemId) {
        return false;
    }
    const item = getItemSummary(state, itemId);
    // item.index === 9999 indicates that it was loaded from the similar items endpoint and we should refetch
    if (item.itemId && item.index !== 9999 && item.lotsListed) {
        const loaded = getLoadTimeForItemSummary(state, itemId);
        const time = Date.now();
        const diff = time - loaded;
        const catalogDone = item.catalogStatus === AuctionStatus.Done;
        if (diff < ITEM_SUMMARY_CACHE_TIME || catalogDone) {
            return false;
        }
    }
    const loading = getIsItemSummaryLoading(state, itemId);
    return !loading;
};

export const whichItemSummariesNeeded = (state: GlobalState, itemIds: number[]): number[] =>
    // Added a filter to ensure we don't request item card models for items with the itemId 0
    itemIds.filter((itemId) => Boolean(itemId) && shouldFetchItemSummary(state, itemId));

export const getItemSummaryHouseReviewCountAndRating = createSelector(
    [byIdSelector, idSelector, getItemSummarySellerId, getItemSummarySellerName],
    (byId, itemId, sellerId, sellerName) => {
        const item = byId[itemId];
        return {
            houseRating: item?.houseRating,
            houseReviewCount: item?.houseReviewCount,
            sellerId,
            sellerName,
        };
    }
);
